/**
 * 풀이 화면(SC-04). 디자인 v0.3 gr-choice.md·state-table.md 4장.
 * - GR 지점은 문장 속에 놓는다. 위치·순서는 segments의 지점 출현 순서(= 지점 order, 서버가 배포 시 검사).
 * - 지점 배지는 원문 표시 번호(displayNumber). 정렬 순서는 화면에 숫자로 보이지 않는다.
 * - 보기 순서는 데이터 순서 그대로. 정답 수·정답 여부에 따라 모양·배치가 달라지지 않는다.
 * - 고른 보기를 다시 눌러도 그대로. 미선택으로 되돌리려면 "지우기".
 * - 지점 = role="radiogroup", 보기 = role="radio". 지점 안에서 ←→↑↓는 이동만, Space·Enter가 고르기.
 * DOM은 한 번 만들고 상태가 바뀐 대상만 갱신한다(키보드 초점 유지).
 */
import { classifyOmrInput } from '@growing/domain-rules';
import type { DisplayStatus, SaveManager } from '../core/save-manager.js';
import { targetOrder } from '../core/precheck.js';
import { modesOf, unansweredGuide } from '../core/guide.js';
import { GR_SEP, grIdsOf, type GrPointView, type InputChannel, type ItemView, type ItemsResponse, type TargetValue } from '../core/types.js';
import { h } from './dom.js';

/** GR 공통 지시문(총괄 DEV-I-002 항목 4) */
export const GR_INSTRUCTION = '각 지점에서 어법·문맥에 맞는 보기를 하나 고르세요.';
const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];
const STATUS_WORD: Record<DisplayStatus, string> = {
  empty: '', dirty: '저장 전', saving: '저장 중', saved: '저장됨', failed: '저장되지 않음', conflict: '확인 필요', locked: '제출됨, 바꿀 수 없음',
};

export interface SolveCallbacks {
  onSubmit(): void;
  onExit(): void;
  onRetry(): void;
  onOpenConflicts(): void;
}

interface PointRefs { readonly el: HTMLElement; readonly no: HTMLElement; readonly opts: HTMLButtonElement[]; readonly clr: HTMLButtonElement; readonly point: GrPointView; readonly row: HTMLButtonElement | null }
interface ItemRefs {
  readonly el: HTMLElement; readonly opts: HTMLButtonElement[]; readonly clr: HTMLButtonElement; readonly st: HTMLElement; readonly item: ItemView;
  readonly screenBox: HTMLElement; readonly omrBox: HTMLElement; readonly omrInput: HTMLInputElement; readonly omrClr: HTMLButtonElement; readonly omrErr: HTMLElement;
  row: HTMLButtonElement | null;
}

export class SolveView {
  readonly root: HTMLElement;
  private readonly points = new Map<string, PointRefs>();
  private readonly mcs = new Map<string, ItemRefs>();
  private readonly order: { id: string; kind: 'gr' | 'choice'; itemId: string }[];
  private readonly chip: HTMLElement;
  private readonly band: HTMLElement;
  private readonly prog: HTMLElement;
  private readonly submitButtons: HTMLButtonElement[] = [];
  /** 머리글 "목록 n/m" 단추(좁은 폭). 넓은 폭에서는 CSS가 숨기고 목록을 오른쪽 단에 둔다. */
  private readonly listBtn: HTMLButtonElement;
  private readonly side: HTMLElement;
  /**
   * 목록 패널 열림 상태. 폭이 바뀌어도 화면을 다시 그리지 않으므로 이 값은 그대로 남는다.
   * 넓은 폭(960px 이상)에서는 CSS가 이 값을 무시하고 오른쪽 목록으로 보여 준다(입력 가능). 다시 좁아지면 패널 상태 그대로.
   */
  listOpen = false;
  private offline = false;
  /**
   * 일반 객관식 입력 방식. 과제가 허용한 경로(assignment.inputChannels)에서만 고른다.
   * 하나만 허용하면 그 경로, 둘 다 허용하면 학생이 고르기 전까지 null(문항 번호·화면 규칙으로 정하지 않음).
   */
  private mode: InputChannel | null;
  private readonly modeBar: HTMLElement | null;

  constructor(private readonly data: ItemsResponse, private readonly sm: SaveManager, private readonly cb: SolveCallbacks) {
    this.order = targetOrder(data.items);
    this.chip = h('span', { class: 'chip', 'data-testid': 'save-chip', role: 'status' });
    this.band = h('div', { class: 'band', hidden: true, 'data-testid': 'band' });
    this.prog = h('span', { class: 'prog', 'data-testid': 'progress' });
    const channels = data.assignment.inputChannels;
    this.mode = channels.length === 1 ? channels[0]! : null;
    const hasChoice = data.items.some((it) => it.responseMode === 'single-choice');
    this.modeBar = channels.length > 1 && hasChoice ? this.renderModeBar() : null;

    const main = h('div', { class: 'main' });
    main.append(this.renderGuide());
    if (this.modeBar !== null) main.append(this.modeBar);
    for (const it of [...data.items].sort((a, b) => a.order - b.order)) main.append(this.renderItem(it));
    const side = this.renderSide();
    this.side = side;
    this.listBtn = h('button', {
      class: 'list-btn', 'data-testid': 'list-open', 'aria-expanded': 'false', 'aria-controls': 'list-panel',
      onclick: () => this.setListOpen(!this.listOpen),
    }, '목록');
    const submitFoot = h('button', { class: 'primary', 'data-testid': 'submit', onclick: () => this.cb.onSubmit() }, '제출하기');
    this.submitButtons.push(submitFoot);
    const foot = h('div', { class: 'foot' },
      this.prog,
      h('button', { class: 'next', 'data-testid': 'next-unselected', onclick: () => this.focusNextUnselected() }, '다음 미선택 지점'),
      submitFoot);

    this.root = h('div', { class: 'solve' },
      h('div', { class: 'ahead' },
        h('span', { class: 'mark', 'aria-hidden': 'true' }),
        h('span', { class: 't', 'data-testid': 'release-title' }, data.release.title),
        this.chip,
        this.listBtn,
        h('button', { class: 'exit', 'data-testid': 'exit', onclick: () => this.cb.onExit() }, '나가기')),
      this.band,
      h('div', { class: 'body' }, h('div', { class: 'cols' }, main, side)),
      h('div', { class: 'list-backdrop', 'data-testid': 'list-backdrop', 'aria-hidden': 'true', onclick: () => this.setListOpen(false) }),
      foot);
    this.root.dataset['list'] = 'closed';
    this.root.addEventListener('keydown', (e) => { if (e.key === 'Escape' && this.listOpen) { e.stopPropagation(); this.setListOpen(false); this.listBtn.focus(); } });
    for (const t of this.order) this.updateTarget(t.id);
    this.applyMode();
    this.updateChrome();
  }

  /** 과제 안내: 과제명·마감(한국 시간)·미응답 허용·입력 방식·탭 메모리 보존 범위 */
  private renderGuide(): HTMLElement {
    const a = this.data.assignment;
    const due = a.dueAt === null ? null : `마감 ${formatKst(a.dueAt)} (한국 시간)`;
    return h('div', { class: 'guide', 'data-testid': 'guide' },
      h('b', { 'data-testid': 'assignment-title' }, a.title),
      due !== null ? h('span', { 'data-testid': 'due' }, due) : null,
      h('span', { 'data-testid': 'guide-unanswered' }, unansweredGuide(a.allowUnanswered, modesOf(this.data.items))),
      h('span', { class: 'mem' }, '저장되지 않은 답은 이 탭에만 남아 있어요. 새로고침하거나 탭을 닫으면 사라져요.'));
  }

  private renderModeBar(): HTMLElement {
    const btn = (m: InputChannel, label: string) => h('button', { 'data-mode': m, 'aria-pressed': 'false', 'data-testid': `mode-${m}`, onclick: () => { this.mode = m; this.applyMode(); } }, label);
    return h('div', { class: 'modebar', role: 'group', 'aria-label': '일반 문항 답 입력 방식' },
      h('span', {}, '일반 문항 답 입력 방식'),
      h('div', { class: 'seg' }, btn('screen', '화면에서 고르기'), btn('omr', '종이 OMR 번호 입력')),
      h('span', { class: 'hint', 'data-testid': 'mode-hint' }));
  }

  private applyMode(): void {
    for (const m of this.mcs.values()) {
      m.screenBox.hidden = this.mode !== 'screen';
      m.omrBox.hidden = this.mode !== 'omr';
    }
    if (this.modeBar !== null) {
      for (const b of this.modeBar.querySelectorAll<HTMLButtonElement>('[data-mode]')) b.setAttribute('aria-pressed', String(b.dataset['mode'] === this.mode));
      (this.modeBar.querySelector('.hint') as HTMLElement).textContent = this.mode === null ? '답 입력 방식을 먼저 골라 주세요.' : '';
    }
    for (const id of this.mcs.keys()) this.updateTarget(id);
  }

  // ---------------------------------------------------------------- 렌더링

  private loc(it: ItemView): HTMLElement {
    // 위치 칩: 자료명(배포 판 제목) · 원 자료 쪽 · 문항 출처 표시. 구간명 전용 필드는 아직 없어 표시하지 않는다(추정하지 않음).
    // 구간명(sectionLabel)은 있을 때만. 없으면 숨긴다.
    const parts = [this.data.release.title, it.sectionLabel, it.page !== null ? `${it.page}쪽` : null, it.sourceLabel].filter((x): x is string => x !== null && x !== '');
    return h('span', { class: 'loc', 'data-testid': 'loc' }, parts.join(' · '));
  }

  private renderItem(it: ItemView): HTMLElement {
    const head = h('div', { class: 'item-h' }, h('span', { class: 'no' }, `${it.number}번`), this.loc(it));
    if (it.responseMode === 'gr-points') {
      const byId = new Map(it.grPoints.map((p) => [p.id, p]));
      const gr = h('p', { class: 'gr', 'data-testid': `gr-${it.id}` });
      for (const s of it.segments ?? []) {
        if (s.kind === 'text') { gr.append(document.createTextNode(s.text)); continue; }
        const p = byId.get(s.pointId);
        if (p === undefined) continue; // 서버가 배포 시 검사하므로 생기지 않음
        gr.append(...this.renderPoint(p));
      }
      return h('section', { class: 'item', 'data-item': it.id }, head, h('p', { class: 'instr', 'data-testid': 'gr-instruction' },
        GR_INSTRUCTION + (it.grPoints.some((p) => this.data.pointModes?.[p.id] === 'multiple') ? ' 여러 개를 고르는 지점은 해당하는 보기를 모두 고르세요.' : '')), gr);
    }
    const name = `${it.number}번 문항`;
    const opts = it.options.map((label, i) => h('button', {
      class: 'o', role: 'radio', 'aria-checked': 'false', tabindex: '-1', 'data-value': String(i + 1), 'data-testid': `mc-${it.id}-${i + 1}`,
      onclick: () => this.sm.setValue(it.id, { kind: 'choice', value: i + 1 }),
      onkeydown: (e: KeyboardEvent) => this.groupKey(e, opts, (b) => b.click()),
    }, h('span', { class: 'c', 'aria-hidden': 'true' }, String(i + 1)), h('span', {}, label)));
    const clr = h('button', { class: 'clr', 'data-testid': `clear-${it.id}`, 'aria-label': `${name} 선택 지우기`, hidden: true, onclick: () => this.sm.setValue(it.id, { kind: 'blank' }) }, '선택 지우기');
    const st = h('span', { class: 'st', 'data-testid': `st-${it.id}` });
    const screenBox = h('div', { class: 'mc-screen' }, h('div', { class: 'mc', role: 'radiogroup', 'aria-label': name }, ...opts), h('div', { class: 'mc-row' }, clr));
    // 종이 OMR 번호 입력(경로 omr). 입력한 원본 그대로 저장하고, 분류는 서버와 같은 도메인 규칙으로 안내만 한다.
    const omrInput = h('input', {
      inputmode: 'numeric', autocomplete: 'off', maxlength: '3', 'aria-label': `${name} OMR 답 번호`, 'data-testid': `omr-${it.id}`,
      oninput: (e: Event) => this.sm.setValue(it.id, { kind: 'omr', raw: (e.target as HTMLInputElement).value }),
    });
    const omrClr = h('button', { class: 'clr', 'data-testid': `omr-clear-${it.id}`, 'aria-label': `${name} OMR 입력 지우기`, onclick: () => { omrInput.value = ''; this.sm.setValue(it.id, { kind: 'omr', raw: '' }); } }, '지우기');
    const omrErr = h('p', { class: 'err', 'data-testid': `omr-err-${it.id}`, role: 'status' });
    const omrBox = h('div', { class: 'omr-wrap' },
      h('div', { class: 'omr' }, h('span', {}, `종이 문제지 ${it.page !== null ? `${it.page}쪽 ` : ''}${it.number}번 답`), omrInput, omrClr),
      omrErr);
    const el = h('section', { class: 'box item', 'data-item': it.id, 'data-target': it.id },
      head,
      h('p', { class: 'q' }, it.stem ?? ''),
      screenBox, omrBox, st);
    this.mcs.set(it.id, { el, opts, clr, st, item: it, screenBox, omrBox, omrInput, omrClr, omrErr, row: null });
    return el;
  }

  private renderPoint(p: GrPointView): Node[] {
    const opts = [...p.options].sort((a, b) => a.order - b.order);
    const buttons: HTMLButtonElement[] = [];
    const children: Node[] = [];
    const multiple = this.data.pointModes?.[p.id] === 'multiple';
    opts.forEach((o, i) => {
      if (i > 0) children.push(h('span', { class: 'sep', 'aria-hidden': 'true' }, ' / '));
      const b = h('button', {
        class: 'opt', role: multiple ? 'checkbox' : 'radio', 'aria-checked': 'false', tabindex: '-1', 'data-opt': o.id, 'data-testid': `opt-${o.id}`,
        onclick: () => {
          if (!multiple) { this.sm.setValue(p.id, o.id); return; }
          const cur = grIdsOf(this.sm.get(p.id).screen as string | null);
          const next = cur.includes(o.id) ? cur.filter((x) => x !== o.id) : [...cur, o.id];
          this.sm.setValue(p.id, next.length === 0 ? null : next.sort().join(GR_SEP));
        },
        onkeydown: (e: KeyboardEvent) => this.groupKey(e, buttons, (x) => x.click()),
      }, o.label);
      buttons.push(b);
      children.push(b);
    });
    const no = h('span', { class: 'pt-no', 'aria-hidden': 'true' }, p.displayNumber);
    // 긴 보기는 보기 단추 안에서 줄바꿈한다. 번호 배지·여는 괄호와 첫 보기, 마지막 보기와 닫는 괄호는 한 덩어리로 둔다
    // (괄호만 다음 줄로 떨어지지 않게). 태블릿 디자인(디자인팀 tablet-verification) 반영.
    const head = h('span', { class: 'pt-head' }, no, h('span', { class: 'br', 'aria-hidden': 'true' }, '['), ...(children.length > 0 ? [children[0]!] : []));
    const close = h('span', { class: 'br', 'aria-hidden': 'true' }, ']');
    const middle = children.slice(1, children.length > 1 ? -1 : 1);
    const tail = h('span', { class: 'pt-tail' }, ...(children.length > 1 ? [children[children.length - 1]!] : []), close);
    const el = h('span', { class: 'pt', role: multiple ? 'group' : 'radiogroup', 'data-multiple': String(multiple), 'data-s': 'empty', 'data-point': p.id, 'data-testid': `pt-${p.id}`, 'data-target': p.id },
      head, ...middle, tail);
    const clr = h('button', { class: 'clr', 'data-testid': `clear-${p.id}`, 'aria-label': `${p.displayNumber}번 지점 선택 지우기`, hidden: true, onclick: () => this.sm.setValue(p.id, null) }, '지우기');
    this.points.set(p.id, { el, no, opts: buttons, clr, point: p, row: null });
    return [el, clr];
  }

  /**
   * 지점·문항 목록. DOM 하나를 폭에 따라 CSS가 다르게 놓는다.
   * 넓은 폭(960px 이상): 오른쪽 단. 좁은 폭: 머리글 "목록" 단추로 여는 패널(항목을 누르면 닫히고 그 위치로 이동).
   */
  private renderSide(): HTMLElement {
    const row = (id: string, n: string) => h('button', { 'data-row': id, onclick: () => { this.setListOpen(false); this.focusTarget(id, false); } },
      h('span', { class: 'n' }, n), h('span', { class: 'v' }), h('span', { class: 's' }));
    const plist = h('div', { class: 'plist', 'data-testid': 'point-list' });
    const ilist = h('div', { class: 'plist', 'data-testid': 'item-list' });
    for (const t of this.order) {
      if (t.kind === 'gr') {
        const refs = this.points.get(t.id)!;
        const r = row(t.id, refs.point.displayNumber);
        (refs as { row: HTMLButtonElement | null }).row = r;
        plist.append(r);
      } else {
        const refs = this.mcs.get(t.id)!;
        refs.row = row(t.id, `${refs.item.number}번`);
        ilist.append(refs.row);
      }
    }
    const submit = h('button', { class: 'primary', 'data-testid': 'submit-side', onclick: () => { this.setListOpen(false); this.cb.onSubmit(); } }, '제출하기');
    this.submitButtons.push(submit);
    const hasGr = plist.childElementCount > 0;
    const hasChoice = ilist.childElementCount > 0;
    return h('aside', { class: 'side', id: 'list-panel', 'data-testid': 'list-panel', 'aria-label': '지점과 문항 목록' },
      h('div', { class: 'list-h' }, h('h2', {}, hasGr && hasChoice ? '지점과 문항' : hasGr ? '지점' : '문항'),
        h('button', { class: 'secondary', 'data-testid': 'list-close', onclick: () => { this.setListOpen(false); this.listBtn.focus(); } }, '닫기')),
      ...(hasGr ? [h('h4', {}, '지점'), plist] : []),
      ...(hasChoice ? [h('h4', {}, '문항'), ilist] : []),
      submit);
  }

  /** 목록 패널 열고 닫기(좁은 폭에서만 보임). 화면을 다시 그리지 않는다. */
  setListOpen(open: boolean): void {
    this.listOpen = open;
    this.root.dataset['list'] = open ? 'open' : 'closed';
    this.listBtn.setAttribute('aria-expanded', String(open));
  }

  /** 지점·문항 안 키보드: 이동만 하고 고르지 않는다. Space·Enter는 버튼 기본 동작(click)으로 고른다. */
  private groupKey(e: KeyboardEvent, group: HTMLButtonElement[], _select: (b: HTMLButtonElement) => void): void {
    const i = group.indexOf(e.currentTarget as HTMLButtonElement);
    let j = -1;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') j = Math.min(group.length - 1, i + 1);
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') j = Math.max(0, i - 1);
    else return;
    e.preventDefault();
    for (const b of group) b.tabIndex = -1;
    group[j]!.tabIndex = 0;
    group[j]!.focus();
  }

  // ---------------------------------------------------------------- 갱신

  valueLabel(id: string, v: TargetValue): string {
    const p = this.points.get(id);
    if (p !== undefined) return v === null ? '고르지 않음' : grIdsOf(v as string).map((id) => p.point.options.find((o) => o.id === id)?.label ?? '알 수 없는 보기').join(', ');
    const m = this.mcs.get(id);
    if (m === undefined || v === null || typeof v === 'string') return '';
    if (v.kind === 'blank') return '답하지 않음';
    if (v.kind === 'choice') return `${CIRCLED[v.value - 1] ?? v.value} ${m.item.options[v.value - 1] ?? ''}`.trim();
    if (v.kind === 'omr') {
      if (v.raw.trim() === '') return 'OMR 입력 없음';
      return classifyOmrInput(v.raw, Math.max(1, m.item.options.length)).state === 'input-error' ? `OMR 입력 오류(${v.raw})` : `OMR ${v.raw.trim()}번`;
    }
    return v.state === 'input-error' ? `입력 오류(${v.raw ?? ''})` : v.state === 'dont-know' ? '모름' : v.state;
  }

  /** 대상 위치 이름: "12번 지점" / "4번 문항" */
  targetName(id: string): string {
    const p = this.points.get(id);
    if (p !== undefined) return `${p.point.displayNumber}번 지점`;
    const m = this.mcs.get(id);
    return m !== undefined ? `${m.item.number}번 문항` : '';
  }

  /** 충돌 화면의 앞뒤 문맥 한 줄 */
  contextOf(id: string): string {
    for (const it of this.data.items) {
      if (it.responseMode !== 'gr-points') continue;
      const segs = it.segments ?? [];
      const i = segs.findIndex((s) => s.kind === 'point' && s.pointId === id);
      if (i < 0) continue;
      const before = segs[i - 1]?.kind === 'text' ? (segs[i - 1] as { text: string }).text.slice(-40) : '';
      const after = segs[i + 1]?.kind === 'text' ? (segs[i + 1] as { text: string }).text.slice(0, 40) : '';
      return `…${before}[ ${this.points.get(id)!.point.displayNumber} ]${after}…`;
    }
    return this.mcs.get(id)?.item.stem ?? '';
  }

  updateTarget(id: string): void {
    const s = this.sm.status(id);
    const t = this.sm.get(id);
    const locked = s === 'locked';
    const p = this.points.get(id);
    if (p !== undefined) {
      const dataS = s === 'empty' ? 'empty' : s;
      p.el.dataset['s'] = dataS;
      let tabSet = false;
      for (const b of p.opts) {
        const on = grIdsOf(t.screen as string | null).includes(b.dataset['opt'] ?? '');
        b.setAttribute('aria-checked', String(on));
        b.disabled = locked;
        b.tabIndex = on && !tabSet ? 0 : -1;
        if (on) tabSet = true;
      }
      if (!tabSet && p.opts[0] !== undefined) p.opts[0].tabIndex = 0;
      // 번호 배지 옆 표시: 저장 전 빈 원, 저장 중 회전, 저장 안 됨 '!', 충돌 '! 확인', 잠김 자물쇠
      const flag = s === 'dirty' ? h('span', { class: 'ring' }) : s === 'saving' ? h('span', { class: 'spin' })
        : s === 'failed' ? h('span', { class: 'pt-flag' }, '!') : s === 'conflict' ? h('span', { class: 'pt-flag' }, '! 확인')
          : s === 'locked' && this.lockLabel === '제출 완료' ? h('span', { class: 'pt-flag', 'aria-hidden': 'true' }, '🔒') : null;
      p.no.replaceChildren(p.point.displayNumber, ...(flag ? [flag] : []));
      p.clr.hidden = t.screen === null || locked || this.sm.isLocked();
      const sel = t.screen === null ? '고르지 않음' : `${this.valueLabel(id, t.screen)} 선택됨`;
      p.el.setAttribute('aria-label', [`${p.point.displayNumber}번 지점`, sel, `보기 ${p.opts.length}개`, this.word(s)].filter(Boolean).join(', '));
      if (p.row !== null) {
        const v = p.row.querySelector('.v') as HTMLElement;
        v.textContent = t.screen === null ? '고르지 않음' : this.valueLabel(id, t.screen);
        v.classList.toggle('empty', t.screen === null);
        const st = p.row.querySelector('.s') as HTMLElement;
        st.textContent = this.word(s);
        st.dataset['s'] = s;
      }
    }
    const m = this.mcs.get(id);
    if (m !== undefined) {
      const v = t.screen;
      let tabSet = false;
      for (const b of m.opts) {
        const on = v !== null && typeof v === 'object' && v.kind === 'choice' && String(v.value) === b.dataset['value'];
        b.setAttribute('aria-checked', String(on));
        b.disabled = locked;
        b.tabIndex = on ? 0 : -1;
        if (on) tabSet = true;
      }
      if (!tabSet && m.opts[0] !== undefined) m.opts[0].tabIndex = 0;
      m.clr.hidden = v === null || (typeof v === 'object' && v.kind !== 'choice') || locked;
      // OMR 입력: 원본을 그대로 보여 주고(입력 중이면 건드리지 않음) 입력 오류를 안내한다
      const raw = v !== null && typeof v === 'object' && v.kind === 'omr' ? v.raw : '';
      if (document.activeElement !== m.omrInput && m.omrInput.value !== raw) m.omrInput.value = raw;
      m.omrInput.disabled = locked;
      m.omrClr.hidden = raw === '' || locked;
      const omrBad = raw.trim() !== '' && classifyOmrInput(raw, Math.max(1, m.item.options.length)).state === 'input-error';
      m.omrInput.dataset['err'] = omrBad ? '1' : '0';
      m.omrErr.textContent = omrBad ? `1~${m.item.options.length} 사이 번호 하나를 입력해 주세요. 지금 입력: ${raw}` : '';
      // 지금 입력 방식과 다른 경로로 저장된 값은 사실만 알린다(바꾸지 않음)
      const otherPath = v !== null && typeof v === 'object' && (
        (this.mode === 'omr' && v.kind === 'choice') || (this.mode === 'screen' && v.kind === 'omr' && raw !== '') || v.kind === 'other');
      m.st.textContent = this.word(s) + (otherPath ? ` · 저장된 값: ${this.valueLabel(id, v)}` : '');
      m.st.dataset['s'] = omrBad ? 'err' : s;
      if (m.row !== null) {
        const empty = v === null || (typeof v === 'object' && ((v.kind === 'omr' && v.raw.trim() === '') || v.kind === 'blank'));
        const rv = m.row.querySelector('.v') as HTMLElement;
        rv.textContent = empty ? '답하지 않음' : this.valueLabel(id, v);
        rv.classList.toggle('empty', empty);
        const rs = m.row.querySelector('.s') as HTMLElement;
        rs.textContent = omrBad ? '입력 오류' : this.word(s);
        rs.dataset['s'] = omrBad ? 'failed' : s;
      }
    }
    this.updateChrome();
  }

  setOffline(v: boolean): void {
    if (this.offline === v) return;
    this.offline = v;
    this.updateChrome();
  }

  updateChrome(): void {
    const c = this.sm.counts();
    this.chip.replaceChildren();
    const locked = this.sm.isLocked();
    if (locked) {
      this.chip.dataset['k'] = 'lock';
      this.chip.textContent = this.lockLabel;
    } else if (c.conflict > 0) {
      this.chip.dataset['k'] = 'warn';
      this.chip.append(`확인 필요 ${c.conflict} · `, h('button', { onclick: () => this.cb.onOpenConflicts() }, '보기'));
    } else if (c.failed > 0) {
      this.chip.dataset['k'] = 'warn';
      this.chip.append(`저장 안 됨 ${c.failed} · `, h('button', { 'data-testid': 'retry', onclick: () => this.cb.onRetry() }, '다시 시도'));
    } else if (c.saving > 0) {
      this.chip.dataset['k'] = 'saving';
      this.chip.textContent = '저장 중';
    } else if (c.dirty > 0) {
      this.chip.dataset['k'] = 'saving';
      this.chip.textContent = `저장 전 ${c.dirty}`;
    } else {
      this.chip.dataset['k'] = 'ok';
      this.chip.textContent = c.saved > 0 ? '모두 저장됨' : '';
    }
    this.chip.hidden = this.chip.textContent === ''; // 표시할 상태가 없으면 빈 칩을 보이지 않음
    if (this.offline) {
      this.band.hidden = false;
      this.band.dataset['k'] = 'warn';
      this.band.textContent = '연결이 끊겼어요. 고른 답은 이 화면에 남아 있어요.';
    } else if (c.failed > 0 && !locked) {
      this.band.hidden = false;
      this.band.dataset['k'] = 'warn';
      this.band.replaceChildren(`저장 안 됨 ${c.failed}`, h('button', { onclick: () => this.cb.onRetry() }, '다시 시도'));
    } else {
      this.band.hidden = true;
    }
    const grIds = this.order.filter((t) => t.kind === 'gr').map((t) => t.id);
    const picked = grIds.filter((id) => this.sm.get(id).screen !== null).length;
    this.prog.textContent = grIds.length > 0 ? `고른 지점 ${picked} / ${grIds.length}` : '';
    // 목록 단추: GR이 있으면 고른 지점 수, 없으면 답한 문항 수
    if (this.listBtn !== undefined) {
      const choiceIds = this.order.filter((t) => t.kind === 'choice').map((t) => t.id);
      const answered = choiceIds.filter((id) => { const v = this.sm.get(id).screen; return !(v === null || (typeof v === 'object' && ((v.kind === 'omr' && v.raw.trim() === '') || v.kind === 'blank'))); }).length;
      this.listBtn.textContent = grIds.length > 0 ? `목록 ${picked}/${grIds.length}` : `목록 ${answered}/${choiceIds.length}`;
    }
    for (const b of this.submitButtons) b.disabled = locked;
  }

  lockLabel = '제출 완료';

  /** 잠김 문구: 제출 완료(S-6)와 제출 확인 중(S-7)을 구분한다 */
  private word(s: DisplayStatus): string {
    if (s !== 'locked') return STATUS_WORD[s];
    return this.lockLabel === '제출 완료' ? '제출됨, 바꿀 수 없음' : '제출 확인 중, 바꿀 수 없음';
  }

  // ---------------------------------------------------------------- 이동

  /** 대상으로 스크롤하고 초점을 옮긴다. flash: 제출 거부 위치 강조(1회) */
  focusTarget(id: string, flash: boolean): void {
    const p = this.points.get(id);
    const el = p?.el ?? this.mcs.get(id)?.el;
    if (el === undefined) return;
    el.scrollIntoView({ block: 'center', behavior: 'auto' });
    const target = p !== undefined ? (p.opts.find((b) => b.tabIndex === 0) ?? p.opts[0]) : (this.mcs.get(id)!.opts.find((b) => b.tabIndex === 0) ?? this.mcs.get(id)!.opts[0]);
    target?.focus();
    if (flash) {
      el.classList.remove('flash');
      void el.offsetWidth;
      el.classList.add('flash');
      setTimeout(() => el.classList.remove('flash'), 1200);
    }
  }

  /** 지금 초점이 있는 지점 다음의 미선택 지점(segments 출현 순서, 끝에서 처음으로) */
  focusNextUnselected(): string | null {
    const grs = this.order.filter((t) => t.kind === 'gr').map((t) => t.id);
    if (grs.length === 0) return null;
    const active = document.activeElement?.closest('[data-point]')?.getAttribute('data-point') ?? null;
    const start = active === null ? -1 : grs.indexOf(active);
    for (let k = 1; k <= grs.length; k++) {
      const id = grs[(start + k) % grs.length]!;
      if (this.sm.get(id).screen === null) { this.focusTarget(id, false); return id; }
    }
    return null;
  }
}

/** ISO 시각을 한국 시간으로: "10월 25일 (토) 21:00" */
export function formatKst(iso: string): string {
  const d = new Date(iso);
  const p = Object.fromEntries(new Intl.DateTimeFormat('ko-KR', { timeZone: 'Asia/Seoul', month: 'numeric', day: 'numeric', weekday: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(d).map((x) => [x.type, x.value]));
  return `${p['month']}월 ${p['day']}일 (${p['weekday']}) ${p['hour']}:${p['minute']}`;
}
